import React from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import "./RainbowEnergyRing.scss";
import BlurTextAnimation from "./BlurTextAnimation";

const containerVariants = {
  active: {
    opacity: 1,
    scale: 1,
    transition: {
      duration: 0.3,
      ease: "easeOut" as const,
    },
  },
  paused: {
    opacity: 0.6,
    scale: 0.95,
    transition: {
      duration: 0.8,
      ease: "easeInOut" as const,
    },
  },
  idle: {
    opacity: 0,
    scale: 0.8,
    transition: {
      duration: 1,
      ease: "easeInOut" as const,
    },
  },
};

// ✨ Variantes para o texto "Thinking"
const textVariants = {
  active: {
    opacity: 1,
    x: 0,
    transition: {
      duration: 0.3,
      ease: "easeOut" as const,
    },
  },
  paused: {
    opacity: 0.6,
    x: -5, // Pequeno movimento para indicar pausa
    transition: {
      duration: 0.8,
      ease: "easeInOut" as const,
    },
  },
  idle: {
    opacity: 0,
    x: -10,
    transition: {
      duration: 1,
      ease: "easeInOut" as const,
    },
  },
};

interface RainbowEnergyRingProps {
  size?: number;
  strokeWidth?: number;
  isActive?: boolean;
  isPaused?: boolean | null;
  speed?: number;
  showText?: boolean; // ✨ Nova prop para controlar se mostra o texto
  textPosition?: "right" | "left" | "top" | "bottom"; // ✨ Posição do texto
}

const RainbowEnergyRing: React.FC<RainbowEnergyRingProps> = ({
  size = 25,
  strokeWidth = 3,
  isActive = true,
  isPaused = false,
  speed = 2,
  showText = true, // ✨ Por padrão mostra o texto
  textPosition = "right", // ✨ Por padrão à direita
}) => {
  const { t } = useTranslation();
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const LOOP_DURATION = 4 / speed;

  // 🔑 Função para determinar o estado da animação
  const getAnimationState = () => {
    if (isActive) return "active";
    if (isPaused) return "paused";
    return "idle";
  };

  // ✨ Função para calcular a posição do texto
  const getTextPositionStyle = () => {
    const spacing = 10; // Espaço entre o anel e o texto
    const positions = {
      right: {
        left: size + spacing,
        top: "50%",
        transform: "translateY(-50%)",
      },
      left: {
        right: size + spacing,
        top: "50%",
        transform: "translateY(-50%)",
      },
      top: {
        left: "50%",
        bottom: size + spacing,
        transform: "translateX(-50%)",
      },
      bottom: {
        left: "50%",
        top: size + spacing,
        transform: "translateX(-50%)",
      },
    };
    return positions[textPosition];
  };

  // Animação principal do gradiente rotativo
  const ringVariants = {
    active: {
      rotate: 360,
      transition: {
        duration: 4 / speed,
        repeat: Infinity,
        ease: "linear" as const,
      },
    },
    paused: {
      // 🔑 Desacelera gradualmente até parar
      transition: {
        duration: 1.2,
        ease: [0.43, 0.13, 0.23, 0.96] as const, // cubic-bezier para desaceleração suave
      },
    },
    idle: {
      rotate: 0,
    },
  };

  // Animação do brilho pulsante
  const glowVariants = {
    active: {
      opacity: [0.6, 1, 0.8, 1, 0.6],
      scale: [1, 1.02, 1],
      transition: {
        duration: 3 / speed,
        repeat: Infinity,
        ease: "easeInOut" as const,
      },
    },
    paused: {
      // 🔑 Diminui brilho gradualmente
      opacity: 0.4,
      scale: 1,
      transition: {
        duration: 0.8,
        ease: [0.25, 0.46, 0.45, 0.94] as const, // easeOutQuad para suavidade
      },
    },
    idle: {
      opacity: 0.4,
      scale: 1,
    },
  };

  // Animação das partículas de energia
  const particleVariants = {
    active: {
      strokeDashoffset: [circumference, 0],
      opacity: [0.8, 1, 0.6, 1, 0.8],
      transition: {
        strokeDashoffset: {
          duration: 6 / speed,
          repeat: Infinity,
          ease: "linear" as const,
        },
        opacity: {
          duration: 2 / speed,
          repeat: Infinity,
          ease: "easeInOut" as const,
        },
      },
    },
    paused: {
      // 🔑 Partículas desaparecem gradualmente
      opacity: 0.3,
      transition: {
        duration: 1.0,
        ease: [0.25, 0.46, 0.45, 0.94] as const,
      },
    },
    idle: {
      strokeDashoffset: circumference,
      opacity: 0.4,
    },
  };

  // Animação das ondas ping
  const createPingVariants = (delay: number) => ({
    active: {
      opacity: [0, 0.6, 0.4, 0],
      scale: [0.5, 2.5],
      transition: {
        repeat: Infinity,
        repeatType: "loop" as const,
        times: [0, 0.5, 0.75, 1],
        duration: LOOP_DURATION,
        ease: "easeOut" as const,
        delay,
      },
    },
    paused: {
      opacity: 0,
      scale: 1.2, // Pausa em uma escala intermediária
      transition: {
        duration: 0.6 + delay * 0.1, // Cada onda para em tempo ligeiramente diferente
        ease: [0.43, 0.13, 0.23, 0.96] as const, // Desaceleração suave
      },
    },
    idle: {
      opacity: 0,
      scale: 0.5,
    },
  });

  return (
    <motion.div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        top: 20,
        left: 15,
      }}
    >
      <motion.div
        className="rainbow-energy-ring-container"
        variants={containerVariants}
        animate={getAnimationState()}
        style={{
          width: size,
          height: size,
          position: "relative",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {/* SVG principal com gradientes */}
        <svg width={size} height={size} className="ring-svg">
          {/* Definições de gradientes */}
          <defs>
            {/* Gradiente arco-íris principal */}
            <linearGradient id="rainbowGradient" gradientUnits="userSpaceOnUse">
              <stop offset="0%" stopColor="oklch(0.7 0.3 240)" /> {/* Azul */}
              <stop offset="16.66%" stopColor="oklch(0.75 0.25 200)" />{" "}
              {/* Ciano */}
              <stop offset="33.33%" stopColor="oklch(0.8 0.2 160)" />{" "}
              {/* Verde */}
              <stop offset="50%" stopColor="oklch(0.85 0.25 90)" />{" "}
              {/* Amarelo */}
              <stop offset="66.66%" stopColor="oklch(0.75 0.3 40)" />{" "}
              {/* Laranja */}
              <stop offset="83.33%" stopColor="oklch(0.7 0.35 10)" />{" "}
              {/* Vermelho */}
              <stop offset="100%" stopColor="oklch(0.75 0.25 330)" />{" "}
              {/* Rosa */}
            </linearGradient>

            {/* Gradiente para efeito de fluxo */}
            <linearGradient id="flowGradient" gradientUnits="userSpaceOnUse">
              <stop offset="0%" stopColor="oklch(0.8 0.3 240 / 0.8)" />
              <stop offset="25%" stopColor="oklch(0.85 0.25 200 / 1)" />
              <stop offset="50%" stopColor="oklch(0.9 0.2 160 / 0.6)" />
              <stop offset="75%" stopColor="oklch(0.85 0.25 90 / 1)" />
              <stop offset="100%" stopColor="oklch(0.8 0.3 40 / 0.8)" />
            </linearGradient>

            {/* Filtro de brilho */}
            <filter id="glow">
              <feGaussianBlur stdDeviation="3" result="coloredBlur" />
              <feMerge>
                <feMergeNode in="coloredBlur" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
          </defs>

          {/* Anel base com brilho */}
          <motion.circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke="url(#rainbowGradient)"
            strokeWidth={strokeWidth}
            filter="url(#glow)"
            className="base-ring"
            variants={glowVariants}
            animate={getAnimationState()}
          />

          {/* Anel rotativo para movimento */}
          <motion.g
            variants={ringVariants}
            animate={getAnimationState()}
            style={{ transformOrigin: `${size / 2}px ${size / 2}px` }}
          >
            <circle
              cx={size / 2}
              cy={size / 2}
              r={radius}
              fill="none"
              stroke="url(#flowGradient)"
              strokeWidth={strokeWidth / 2}
              opacity="0.8"
            />
          </motion.g>

          {/* Partículas de energia - traços que seguem o anel */}
          <motion.circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke="oklch(0.9 0.15 200 / 0.8)"
            strokeWidth={1}
            strokeDasharray={`${circumference * 0.1} ${circumference * 0.9}`}
            variants={particleVariants}
            animate={getAnimationState()}
            className="energy-particles"
          />
        </svg>

        {/* Brilho adicional com CSS */}
        <motion.div
          className="outer-glow"
          variants={glowVariants}
          animate={getAnimationState()}
          style={{
            width: size * 1.2,
            height: size * 1.2,
          }}
        />

        {/* Ondas ping expandindo */}
        <motion.div
          className="ping-wave"
          variants={createPingVariants(0)}
          animate={getAnimationState()}
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            width: size,
            height: size,
            borderRadius: "50%",
            border: "1px solid oklch(0.7 0.3 240 / 0.6)",
            background:
              "radial-gradient(circle, oklch(0.7 0.3 240 / 0.2) 0%, oklch(0.8 0.25 200 / 0.1) 50%, transparent 70%)",
            transform: "translate(-50%, -50%)",
            pointerEvents: "none",
          }}
        />
        <motion.div
          className="ping-wave"
          variants={createPingVariants(LOOP_DURATION * 0.25)}
          animate={getAnimationState()}
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            width: size,
            height: size,
            borderRadius: "50%",
            border: "1px solid oklch(0.75 0.25 200 / 0.6)",
            background:
              "radial-gradient(circle, oklch(0.75 0.25 200 / 0.2) 0%, oklch(0.8 0.2 160 / 0.1) 50%, transparent 70%)",
            transform: "translate(-50%, -50%)",
            pointerEvents: "none",
          }}
        />
        <motion.div
          className="ping-wave"
          variants={createPingVariants(LOOP_DURATION * 0.5)}
          animate={getAnimationState()}
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            width: size,
            height: size,
            borderRadius: "50%",
            border: "1px solid oklch(0.8 0.2 160 / 0.6)",
            background:
              "radial-gradient(circle, oklch(0.8 0.2 160 / 0.2) 0%, oklch(0.85 0.25 90 / 0.1) 50%, transparent 70%)",
            transform: "translate(-50%, -50%)",
            pointerEvents: "none",
          }}
        />
        <motion.div
          className="ping-wave"
          variants={createPingVariants(LOOP_DURATION * 0.75)}
          animate={getAnimationState()}
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            width: size,
            height: size,
            borderRadius: "50%",
            border: "1px solid oklch(0.85 0.25 90 / 0.6)",
            background:
              "radial-gradient(circle, oklch(0.85 0.25 90 / 0.2) 0%, oklch(0.75 0.3 40 / 0.1) 50%, transparent 70%)",
            transform: "translate(-50%, -50%)",
            pointerEvents: "none",
          }}
        />
      </motion.div>

      {/* ✨ Texto "Thinking" */}
      {showText && (
        <motion.div
          className="thinking-text-container"
          variants={textVariants}
          animate={getAnimationState()}
          style={{
            ...getTextPositionStyle(),
            fontSize: "12px",
            color: "oklch(0.9 0.15 200 / 0.4)",
            pointerEvents: "none",
            textTransform: "uppercase",
            letterSpacing: "2px",
          }}
        >
          {/* <AnimatedText
           text={t("components.quinckQuincy.Home.thinking")}
           style={{ fontWeight: "lighter" }}
           animationState={getAnimationState()}
         /> */}
          <BlurTextAnimation
            text={t("components.quinckQuincy.Home.thinking")}
            animationState="active"
            animateBy="letter"
          />
        </motion.div>
      )}
    </motion.div>
  );
};

export default RainbowEnergyRing;
